import React, {
  ReactNode,
} from 'react';
import {
  AxiosError,
} from 'axios';
import values from 'lodash/values';
import uniqueId from 'lodash/uniqueId';

type CommonErrorDataResponse = {
  errors: Record<string, string[]>;
};

type ErrorResponse = {
  Message: string;
};

type ExternalErrorResponse = {
  message: string;
};

type ErrorItem = Partial<{
  id: string;
  message: string;
}>;

export const NO_ACCESS_DETAIL = 'It may have been deleted, or you may not have access to it.';

export function getNoAccessTitle(recordName = 'record') {
  return `This ${recordName} isn't available`;
}

export const NO_ACCESS_MESSAGE = `${getNoAccessTitle()}. ${NO_ACCESS_DETAIL}`;

export type NoAccessOptions = {
  // Throws a 403/404 to the panel error boundary
  throwOnNoAccess?: boolean;
};

// 404 covers both a missing record and another org's record, so IDs can't be probed
export function isNoAccessError(error: unknown): boolean {
  const status = (error as AxiosError | null)?.response?.status;
  return status === 403 || status === 404;
}

function getErrorText(value: unknown): string | null {
  const data = value as Partial<ErrorResponse & ExternalErrorResponse> | null;
  const message = typeof value === 'object'
    ? data?.Message ?? data?.message
    : value;

  return typeof message === 'string' ? message : null;
}

export function getFormErrorList(errors: object) {
  return values(errors).map((error: ErrorItem) => ({ ...error, id: uniqueId('error__') }));
}

type ErrorListProps = {
  errors: ErrorItem[];
};

export function ErrorList({ errors }: ErrorListProps) {
  return errors.length > 0
    ? (
      <ul className="list-disc list-inside">
        {errors.map((error) => <li key={error.id}>{getErrorText(error?.message) ?? ''}</li>)}
      </ul>
    )
    : null;
}

export function getErrorMessage(error: unknown): ReactNode | string | null {
  const plainTextError = typeof error === 'string' ? error : null;
  const axiosError = error as AxiosError;
  const data = axiosError?.response?.data;

  const errorsAsObject = typeof data === 'object'
    ? (
      ((data as ErrorResponse)?.Message ?? (data as ExternalErrorResponse)?.message)
      ?? values((data as CommonErrorDataResponse)?.errors)?.flat()?.map(
        (error) => ({
          message: error,
          id: uniqueId('api_error_'),
        }),
      )
    ) : null;

  return (Array.isArray(errorsAsObject) && errorsAsObject?.length > 0)
    ? <ErrorList errors={errorsAsObject} />
    : getErrorText(errorsAsObject) ?? plainTextError ?? getErrorText(data);
}
